// Fatia S3a: detalhe da campanha (situação, números, anúncios ranqueados, orçamento com estimativa, público e histórico).
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Bot, Image, Minus, Plus, Sparkles, User, Users } from 'lucide-react'
import { num } from '@impulso/shared'
import type { CampaignDetailResponse } from '@impulso/shared/api/insights'
import { ApiError, errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { Delta, ErrorState, HealthBadge, Loading, Platform } from '../../ui'
import { SpendContactsChart } from '../../ui/charts'
import { useToast } from '../../ui/toast'
import { useCampaignDetail, useEstimate, useSetDailyBudget } from '../../features/insights/api'
import { Kpi, money, money2, toChartData, vsLabel } from '../../features/insights/components'
import { CampaignStatusSwitch } from '../../features/campaigns/CampaignStatusSwitch'

export default function CampaignDetail() {
  const { campaignId = '' } = useParams()
  const company = useCompany()
  const q = useCampaignDetail(company.id, campaignId, 30)
  const back = <Link to={`/app/${company.id}/campanhas`} className="row small muted" style={{ gap: 6 }}><ArrowLeft size={15} /> Campanhas</Link>

  if (q.isLoading) return <div className="stack" style={{ gap: 20 }}>{back}<div className="card"><Loading lines={5} /></div></div>
  if (q.isError && q.error instanceof ApiError && q.error.status === 404) {
    return <div className="stack" style={{ gap: 20 }}>{back}<div className="card empty">Campanha não encontrada. <Link to={`/app/${company.id}/campanhas`} className="strong">Voltar</Link></div></div>
  }
  if (q.isError || !q.data) return <div className="stack" style={{ gap: 20 }}>{back}<ErrorState error={q.error} onRetry={() => q.refetch()} /></div>
  return <Detail data={q.data} back={back} />
}

function Detail({ data, back }: { data: CampaignDetailResponse; back: React.ReactNode }) {
  const company = useCompany()
  const c = data.campaign
  const k = data.kpis
  const L = c.resultLabel
  const plural = (n: number) => (n === 1 ? L.one : L.many)
  const hasData = k.spend > 0 || k.impressions > 0
  const vs = vsLabel(30)

  return (
    <div className="stack" style={{ gap: 20 }}>
      {back}

      <div className="page-head" style={{ marginBottom: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div className="row wrap" style={{ gap: 10, marginBottom: 8 }}>
            <Platform platform={c.provider} label={c.channelLabel} />
            <HealthBadge value={c.health} />
          </div>
          <h1 style={{ overflowWrap: 'anywhere' }}>{c.name}</h1>
          <p className="sub">Objetivo: {c.objectiveLabel}</p>
        </div>
        <div className="actions">
          <CampaignStatusSwitch companyId={company.id} campaign={c} showLabel />
        </div>
      </div>

      {(c.health === 'attention' || c.health === 'bad') && (
        <div className={`callout ${c.health === 'bad' ? '' : 'amber'}`} style={c.health === 'bad' ? { background: 'var(--red-bg)' } : undefined}>
          <Sparkles size={18} color={c.health === 'bad' ? 'var(--red)' : 'var(--amber)'} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div className="strong" style={{ color: 'var(--text)' }}>{c.healthReason}</div>
            <div className="row wrap mt-8" style={{ gap: 8 }}>
              <Link to={`/app/${company.id}/sugestoes`} className="btn sm primary">Ver sugestões</Link>
              {c.connectionStatus !== 'active' && <Link to={`/app/${company.id}/contas`} className="btn sm">Reconectar conta</Link>}
            </div>
          </div>
        </div>
      )}
      {c.health !== 'attention' && c.health !== 'bad' && <p className="small muted" style={{ marginTop: -8 }}>{c.healthReason}</p>}

      <div className="grid g-4 g-kpi">
        <Kpi small label="Investido (30 dias)" value={money(k.spend)} delta={<Delta value={k.deltas.spend} />} vs={vs} />
        <Kpi small label={`Resultados (${L.many})`} value={num(k.results)} delta={<Delta value={k.deltas.results} />} vs={vs} />
        <Kpi small label={`Custo por ${L.one}`} value={money2(k.costPerResult)} delta={<Delta value={k.deltas.costPerResult} invert />} vs={vs} />
        <Kpi small label="Viram × clicaram" value={`${num(k.impressions)} · ${k.ctr != null ? `${num(k.ctr * 100, 1)}%` : '—'}`} hint="Vezes que apareceu e % que clicou" />
      </div>

      <div className="grid g-main">
        <div className="stack">
          <div className="card">
            <div className="card-head"><h2>Desempenho diário</h2></div>
            {hasData ? <SpendContactsChart data={toChartData(data.daily)} height={180} /> : <div className="empty compact small">Sem dados nos últimos 30 dias.</div>}
          </div>

          {data.adGroups.length > 1 && (
            <div className="card">
              <h2>{c.provider === 'google_ads' ? 'Grupos de anúncios' : 'Conjuntos de anúncios'}</h2>
              <div className="list mt-12">
                {data.adGroups.map((g) => (
                  <div className="list-item" key={g.id}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="strong">{g.name}</div>
                      {g.targetingSummary && <div className="small muted mt-8">{g.targetingSummary}</div>}
                    </div>
                    <div className="right">
                      <div className="strong">{num(g.results)} {plural(g.results)}</div>
                      <div className="small muted">{money(g.spend)}{g.dailyBudget != null ? ` · ${money(g.dailyBudget)}/dia` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head"><h2>Anúncios</h2></div>
            {data.ads.length === 0 ? <div className="empty compact small">Nenhum anúncio ativo nesta campanha.</div> : (
              <div className="list">
                {data.ads.map((a) => (
                  <div className="list-item" key={a.id}>
                    <div className="thumb" style={{ background: a.imageUrl ? `center / cover no-repeat url("${a.imageUrl}")` : 'linear-gradient(135deg, #7c6bff, #5b3df5)' }}>{!a.imageUrl && <Image size={22} />}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="row wrap" style={{ gap: 8 }}>
                        <span className="strong" style={{ overflowWrap: 'anywhere' }}>{a.headline || a.name}</span>
                        {a.rank === 'best' && <span className="badge green">Melhor anúncio</span>}
                        {a.rank === 'below' && <span className="badge amber">Abaixo da média</span>}
                        {a.policyStatus === 'disapproved' && <span className="badge red">Reprovado</span>}
                        {a.status === 'paused' && <span className="badge">Pausado</span>}
                      </div>
                      <div className="small muted mt-8">{[a.format && formatLabel(a.format), a.adGroupName].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="right">
                      <div className="strong nowrap">{num(a.results)} {plural(a.results)}</div>
                      <div className="small muted">{a.costPerResult != null ? `${money2(a.costPerResult)} cada` : a.spend > 0 ? `${money(a.spend)} gastos` : 'sem dados ainda'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          <BudgetCard data={data} />

          <div className="card">
            <div className="row" style={{ gap: 8, marginBottom: 8 }}><Users size={16} className="muted" /><h3>Quem vê seus anúncios</h3></div>
            <p className="muted">{data.audience ?? 'O público desta campanha ainda não foi sincronizado.'}</p>
          </div>

          <div className="card">
            <h3>Histórico de mudanças</h3>
            <p className="small muted">Tudo o que você e o Seu Impulso fizeram nesta campanha.</p>
            {data.history.length === 0 ? <div className="empty compact small">Nenhuma mudança feita pelo Seu Impulso ainda.</div> : (
              <div className="list mt-12">
                {data.history.map((h) => (
                  <div className="list-item" key={h.id}>
                    <span className={`ic ${h.actor === 'user' ? 'blue' : 'purple'}`} style={{ width: 28, height: 28 }}>
                      {h.actor === 'user' ? <User size={14} /> : <Bot size={14} />}
                    </span>
                    <div>
                      <div className="small"><b>{h.who}</b> <span className="muted">· {new Date(h.at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>{statusText(h.status) && <span className="muted"> · {statusText(h.status)}</span>}</div>
                      <div className="small">{h.summary}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function BudgetCard({ data }: { data: CampaignDetailResponse }) {
  const company = useCompany()
  const canPublish = useCan('company.publish')
  const toast = useToast()
  const c = data.campaign
  const current = c.dailyBudget ?? 0
  const [budget, setBudget] = useState(current)
  const [debounced, setDebounced] = useState(current)
  useEffect(() => setBudget(current), [current])
  useEffect(() => {
    const t = setTimeout(() => setDebounced(budget), 350)
    return () => clearTimeout(t)
  }, [budget])
  const est = useEstimate(company.id, debounced, { campaignId: c.campaignId, enabled: debounced > 0 && debounced !== current })
  const save = useSetDailyBudget(company.id)
  const step = budget >= 200 ? 10 : budget >= 50 ? 5 : 1
  const editable = canPublish && c.canChangeBudget
  const text = debounced !== current ? est.data?.text : data.estimate?.text

  return (
    <div className="card">
      <h3>Orçamento por dia</h3>
      {c.dailyBudget == null && !c.canChangeBudget ? (
        <p className="small muted mt-8">{c.budgetHint ?? 'Esta campanha não tem orçamento diário.'}</p>
      ) : (
        <>
          <div className="row mt-12" style={{ gap: 12 }}>
            {editable && <button className="icon-btn" aria-label="Diminuir" onClick={() => setBudget((b) => Math.max(1, b - step))}><Minus size={16} /></button>}
            <span style={{ fontSize: 26, fontWeight: 700, minWidth: 90, textAlign: 'center' }}>{money(budget)}</span>
            {editable && <button className="icon-btn" aria-label="Aumentar" onClick={() => setBudget((b) => b + step)}><Plus size={16} /></button>}
          </div>
          <p className="small muted mt-8">Cerca de {money(budget * 30)} por mês. {text}</p>
          {!c.canChangeBudget && c.budgetHint && <p className="small muted mt-8">{c.budgetHint}</p>}
          {c.canChangeBudget && !canPublish && <p className="small muted mt-8">Só quem gerencia a empresa pode mudar o orçamento.</p>}
          {editable && budget !== current && (
            <div className="row mt-12" style={{ gap: 8 }}>
              <button className="btn" onClick={() => setBudget(current)} disabled={save.isPending}>Cancelar</button>
              <button
                className="btn primary"
                style={{ flex: 1 }}
                disabled={save.isPending}
                onClick={() => save.mutate({ campaignId: c.campaignId, dailyBudget: budget }, {
                  onSuccess: () => toast(`Pedido enviado: orçamento de ${money(budget)} por dia. Aparece aqui assim que a plataforma confirmar.`),
                  onError: (err) => toast(errorMessage(err)),
                })}
              >
                {save.isPending ? 'Enviando…' : 'Salvar novo orçamento'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function formatLabel(f: string): string {
  return ({ image: 'Imagem', video: 'Vídeo', carousel: 'Carrossel', search_text: 'Anúncio de texto', stories: 'Stories', reels: 'Reels' } as Record<string, string>)[f] ?? f
}

function statusText(s: string): string | null {
  if (['succeeded'].includes(s)) return null
  if (['failed', 'conflict', 'blocked'].includes(s)) return 'não aplicada'
  if (s === 'reverted') return 'desfeita'
  if (s === 'proposed') return 'aguardando aprovação'
  return 'em andamento'
}
